/* eslint-disable arrow-body-style, max-len */

import { get } from 'lodash';
import { createRouter, createWebHistory } from 'vue-router';
import { MODALS } from '@/components/app/app-modals/modals-constants';
import env from '@/env';
import { pushPageview } from '@/helpers/dataLayer';
import ls from '@/helpers/localStorage';
import { reloadOnChunkError, consumePendingChunkReload } from '@/helpers/reloadOnChunkError';
import sessionVars from '@/helpers/sessionVars';
import { isStandalone } from '@/mixins/userAgent';
import routes from '@/router/routes';
import store from '@/store';

export const scrollState = { pendingPosition: null };

const DISABLE_RESTRICTED_ACCESS_ROUTES = ['disqus-auth', 'social-auth', 'home'];

const DISABLE_CHECK_CLASSICS_ACCESS_ROUTES = ['terms-and-conditions'];

const PRERENDER_READY_FALLBACK_MS = 4000;

const RouterInstance = createRouter({
  history: createWebHistory(env.ROOT_PATH),
  scrollBehavior(to, from, savedPosition) {
    if (to && get(to, 'params.dontReload', false)) {
      return null;
    }
    if (to.hash && ['midterms', 'midterms-guid'].includes(to.name)) {
      scrollState.pendingPosition = null;
      return null;
    }
    if (savedPosition) {
      scrollState.pendingPosition = savedPosition;
      return null;
    }
    scrollState.pendingPosition = null;
    return { left: 0, top: 0 };
  },
  routes,
});

RouterInstance.beforeEach(async (to, from) => {
  consumePendingChunkReload();

  if (from && from.name && from.fullPath) {
    store.dispatch('app/setPreviousRoute', from.fullPath);
  }
  if (isStandalone && from.name && to.name && to.fullPath) {
    const expiresAt = 31536000;
    ls.set('currentRoutePath', to.fullPath, expiresAt);
  }

  const shouldCheckAccess = () => {
    return (
      (env.RESTRICTED_ACCESS_ENABLED && !DISABLE_RESTRICTED_ACCESS_ROUTES.includes(to.name)) ||
      to.matched.some((record) => record.meta.requiresAuth)
    );
  };

  const shouldCheckAnonymous = () => {
    return to.matched.some((record) => record.meta.requiresAnonymous);
  };

  const shouldCheckClassicsEnabled = () => {
    return !DISABLE_CHECK_CLASSICS_ACCESS_ROUTES.includes(to.name);
  };

  const serverApiVersion = parseInt(store.state.app.serverVersion || 0, 10);
  const initialApiVersion = parseInt(store.state.app.initialVersion || 0, 10);

  if (serverApiVersion && initialApiVersion && serverApiVersion > initialApiVersion) {
    window.location.reload();
  }

  const hasClassicsNotEnabled = store.state.user.status.isClassicEnabled === false;

  if (shouldCheckAccess()) {
    if (store.getters['user/isLoggedIn'] === false) {
      store.dispatch('popup/setCurrentPopup', MODALS.LOGIN);
      return { name: 'home', query: { redirect: to.fullPath } };
    } else if (hasClassicsNotEnabled) {
      await store.dispatch('popup/setCurrentPopup', MODALS.ACCOUNT_ENABLE_CLASSIC);
      return true;
    }
    await store.dispatch('popup/setCurrentPopup');
    await store.dispatch('popup/setCurrentSmallPopup');
    return true;
  } else if (shouldCheckAnonymous()) {
    if (store.getters['user/isLoggedIn'] === true) {
      return { name: 'home', query: { ...to.query } };
    }
    return true;
  } else if (shouldCheckClassicsEnabled()) {
    if (hasClassicsNotEnabled) {
      await store.dispatch('popup/setCurrentPopup', MODALS.ACCOUNT_ENABLE_CLASSIC);
      return true;
    }
    return true;
  }
  await store.dispatch('popup/setCurrentPopup');
  await store.dispatch('popup/setCurrentSmallPopup');
  return true;
});

RouterInstance.afterEach((to) => {
  sessionVars.check(to);
  if (to && to.fullPath) pushPageview(to.fullPath, to.name);
  setTimeout(() => {
    window.prerenderReady = true;
  }, PRERENDER_READY_FALLBACK_MS);
});

// Routes are lazy-loaded, so a navigation depends on fetching that route's
// chunk. If a deploy replaced the chunk hashes while an old client is still
// open, the fetch 404s; reload once to pull the fresh index.html + chunks.
RouterInstance.onError(reloadOnChunkError);

export default RouterInstance;
